'use client';

import type { LoggerInterface } from '@coolblue-development/logging-abstraction';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { Component, ReactNode, Suspense } from 'react';

/**
 * Error handling based on
 * https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 * https://nextjs.org/docs/pages/building-your-application/configuring/error-handling
 */

type ErrorBoundaryPropsType = {
  fallback?: ReactNode;
  children: ReactNode;
};

type ManagerPropsType = ErrorBoundaryPropsType & {
  logger: LoggerInterface;
};

type StateType = {
  hasError: boolean;
};

const ErrorBoundary = ({ children, fallback }: ErrorBoundaryPropsType) => {
  return (
    <ErrorBoundaryManager logger={useClientLogger()} fallback={fallback}>
      <Suspense>{children}</Suspense>
    </ErrorBoundaryManager>
  );
};

class ErrorBoundaryManager extends Component<ManagerPropsType, StateType> {
  constructor(props: ManagerPropsType) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.logger.error('Error within ErrorBoundary wrapper', error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <></>;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
